/**
 * A checkbox whose whole card is the target, for an option that needs a line
 * of explanation: the multi-choice sibling of `RadioCardGroup`.
 *
 * @module components/ui/Form/CheckboxCard
 */

import React, { type ReactNode, useId } from "react";

import { Checkbox } from "@/components/ui/Checkbox";

import { useLockedRefusal } from "../EditLock";

export interface CheckboxCardProps {
  id: string;
  label: string;
  /** What ticking it means. Shown, not announced, so the name stays short. */
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Why it is disabled: a tooltip on hover/focus, and its description. */
  disabledReason?: string;
  className?: string;
  /** On the checkbox, which is what a test presses. */
  "data-testid"?: string;
}

export function CheckboxCard({
  id,
  label,
  description = undefined,
  checked,
  onChange,
  disabled: ownDisabled = false,
  className = "",
  "data-testid": testId,
  ...reasonProps
}: CheckboxCardProps): React.ReactElement {
  // Left in the rest so its presence is readable: a caller that can refuse
  // with a reason keeps one wrapper throughout, matching `Toggle` and `Button`.
  const { disabled, offersReason, disabledReason } = useLockedRefusal(ownDisabled, reasonProps);
  const reasonId = useId();
  const reason = disabled && disabledReason ? disabledReason : undefined;
  return (
    <label
      htmlFor={id}
      data-tip={reason}
      className={[
        "flex items-start gap-3 rounded-box border p-3",
        checked ? "border-primary-strong bg-accent-soft" : "border-base-300 bg-base-100",
        disabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-surface-hover",
        reason ? "tooltip" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Checkbox
        id={id}
        size="sm"
        className="mt-0.5 shrink-0"
        aria-label={label}
        aria-describedby={reason ? reasonId : undefined}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-testid={testId}
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{label}</span>
        {description != null && (
          <span className="mt-0.5 block text-md text-muted">{description}</span>
        )}
      </span>
      {offersReason && reason ? (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </label>
  );
}
